import { Formik } from "formik"
import React from "react"

import ReservationModal from "@/components/public/venue/reservation_modal"
import { BG_VERY_LIGHT } from "@/constants/colors"
import { type GetPublicReservationQuery, type PublicVenueQuery } from "@/graphql/graphql"
import Logo from "@/pages/public/logo"
import { type ReservationFormValues } from "@/pages/public/venue"
import { type BookingPolicySectionResult, type BookingPolicyTileViewModel } from "@/shared/booking_policy"

import ReservationDetailsCard from "./reservation_details_card"
import ReservationFooter from "./reservation_footer"
import ReservationRulesCard from "./reservation_rules_card"
import ReservationStatusBanner from "./reservation_status_banner"

const STYLES = {
  page: "min-h-screen",
  topBar: "bg-[#171717] h-[50px] flex items-center justify-center w-full",
  contentWrapper: "max-w-[500px] w-full mx-auto pt-[10px]",
  coverImage: "h-[150px] w-full object-cover",
} as const

const PAGE_STYLE = {
  backgroundColor: BG_VERY_LIGHT,
} as const

type OriginalReservation = {
  endTime: string
  id: string | undefined
  peopleAmount: number
  startTime: string
  tableId: string
}

type Venue = NonNullable<GetPublicReservationQuery["public"]["reservation"]>["venue"]
type FullVenue = NonNullable<PublicVenueQuery["public"]["venue"]>

type Props = {
  bookingPolicySection: BookingPolicySectionResult | null
  bookingPolicyTiles: BookingPolicyTileViewModel[]
  canModify: boolean
  cancellationDeadlineLabel: string | null
  coverImage?: string | null
  customerNote?: string | null
  formattedDate: string
  formattedTimeRange: string
  fullVenue: FullVenue | null | undefined
  modifyInitialValues: ReservationFormValues | null
  modifyModalOpen: boolean
  modifyToken: string
  modifyValidationSchema: object | null
  onCancel: () => void
  onModify: () => void
  onModifyClose: () => void
  onModifyComplete: (newReservationId: string) => void
  originalReservation: OriginalReservation | null
  peopleAmount?: number | null
  referenceNumber: string
  showCancelAction: boolean
  status: string
  tableName: string
  timezoneAbbreviation: string
  venue: Venue | null | undefined
  venueLoading: boolean
  venueLogoUrl: string | null
  venueName: string
}

const ReservationPageContent: React.FC<Props> = ({
  bookingPolicySection,
  bookingPolicyTiles,
  canModify,
  cancellationDeadlineLabel,
  coverImage,
  customerNote,
  formattedDate,
  formattedTimeRange,
  fullVenue,
  modifyInitialValues,
  modifyModalOpen,
  modifyToken,
  modifyValidationSchema,
  onCancel,
  onModify,
  onModifyClose,
  onModifyComplete,
  originalReservation,
  peopleAmount,
  referenceNumber,
  showCancelAction,
  status,
  tableName,
  timezoneAbbreviation,
  venue,
  venueLoading,
  venueLogoUrl,
  venueName,
}) => {
  return (
    <div className={STYLES.page} style={PAGE_STYLE} data-testid="public-reservation-container">
      <div className={STYLES.topBar}>
        <Logo variant="with-icon" />
      </div>

      <div className={STYLES.contentWrapper}>
        {coverImage ? <img src={coverImage} className={STYLES.coverImage} alt="" data-testid="cover-image" /> : null}

        <ReservationStatusBanner status={status} />

        <ReservationDetailsCard
          venueName={venueName}
          formattedDate={formattedDate}
          formattedTimeRange={formattedTimeRange}
          tzAbbr={timezoneAbbreviation}
          peopleAmount={peopleAmount}
          tableName={tableName}
          referenceNumber={referenceNumber}
          customerNote={customerNote}
        />

        {venue ? <ReservationRulesCard venue={venue} /> : null}

        <ReservationFooter
          venueName={venueName}
          venueInfo={venue?.info}
          canModify={canModify}
          showCancelAction={showCancelAction}
          modifyLoading={venueLoading}
          cancellationDeadlineLabel={cancellationDeadlineLabel}
          onModify={onModify}
          onCancel={onCancel}
        />
      </div>

      {modifyModalOpen && fullVenue && modifyInitialValues && modifyValidationSchema ? (
        <Formik<ReservationFormValues>
          initialValues={modifyInitialValues}
          validationSchema={modifyValidationSchema}
          onSubmit={() => {}}
          enableReinitialize={false}
        >
          <ReservationModal
            open={modifyModalOpen}
            onClose={onModifyClose}
            venueSlug={fullVenue.slug}
            venueName={fullVenue.name}
            venueLogoUrl={venueLogoUrl}
            venuePlans={fullVenue.plans}
            address={fullVenue.info?.address}
            city={fullVenue.info?.city}
            tables={fullVenue.tables}
            openingHours={fullVenue.openingHours || []}
            timezone={fullVenue.timezone}
            country={fullVenue.country}
            minAdvanceBookingTime={fullVenue.reservationConfiguration?.minAdvanceBookingTime}
            holdDurationSeconds={fullVenue.reservationConfiguration?.noshowAutoCancelAfter}
            bookingPolicyTiles={bookingPolicyTiles}
            bookingPolicySection={bookingPolicySection}
            editMode
            originalReservation={originalReservation}
            reservationToken={modifyToken}
            onModifyComplete={onModifyComplete}
          />
        </Formik>
      ) : null}
    </div>
  )
}

export default ReservationPageContent
